import { Button } from "@/ingot";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

/**
 * Dark-themed call-to-action block used at the bottom of every
 * marketing page (Landing, /how-it-works, /materials, /pricing).
 *
 * Background is `--ink` (#0c0a09) overlaid with the `.grid-bg-dark`
 * 32×32 pattern; text is white. Single CTA — bottom-of-page repeats
 * the upload entry-point so a customer who scrolled past doesn't
 * have to scroll back. The secondary "Browse materials" button was
 * removed because the materials page is reachable from the header
 * and pairing two equally-weighted CTAs muddied the signal.
 */
interface FinalCtaProps {
  heading?: string;
  lead?: string;
  primaryLabel?: string;
  primaryTo?: string;
}

export function FinalCta({
  heading,
  lead,
  primaryLabel,
  primaryTo,
}: FinalCtaProps): JSX.Element {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  const h = heading ?? t("home.finalCta.heading");
  const l = lead ?? t("home.finalCta.lead");
  const pLabel = primaryLabel ?? t("home.finalCta.primary");
  const pTo = primaryTo ?? `/${lang}`;

  // Always-dark brand block: dark:bg-bg keeps it dark under `.dark` too
  // (both ~#0c0a09), text light in both — see PlatformFinalCta.
  return (
    <section className="st-cta grid-bg-dark relative overflow-hidden bg-ink text-bg dark:bg-bg dark:text-ink">
      <div className="st-cta-in mx-auto max-w-3xl px-7 py-20 text-center md:py-24">
        <h2 className="st-h2 text-balance text-3xl font-semibold tracking-tight md:text-4xl">
          {h}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-white/70">{l}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={pTo}>
            <Button
              variant="accent"
              size="lg"
              className="st-btn"
              data-testid="final-cta-primary"
            >
              {pLabel}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
